import { AccentBox, ConfirmModal } from 'components/common';
import { Key, useEffect, useState } from 'react';

import {
  Box,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  Flex,
  Icon,
  IconButton,
  TextField,
  Typography,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

import { formatCount } from './utils';

const styles = {
  root: {
    backgroundColor: 'white',
    cursor: 'pointer',
    padding: '$medium',
    width: '100%',
  },
  name: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  option: {
    flex: 1,
    minHeight: 34,
    overflow: 'hidden',
    userSelect: 'none',
  },
};

interface IViewToggleOption {
  accentColor: string;
  createView?: () => void;
  deleteView?: () => void;
  disableDrag?: boolean;
  isEdit: boolean;
  isSelected?: boolean;
  name: string;
  onClick?: () => void;
  totalResults?: number | string;
  updateView?: (name: string) => void;
}

function ViewToggleOption({
  accentColor,
  createView,
  deleteView,
  disableDrag,
  isEdit,
  isSelected,
  name,
  onClick,
  totalResults,
  updateView,
}: IViewToggleOption) {
  const { t } = useTranslation();
  const [isRename, setIsRename] = useState(false);
  const [rename, setRename] = useState(name);
  const [isConfirm, setIsConfirm] = useState(false);

  // Lock All Shipments && All Orders
  const isLocked = !(createView && deleteView && updateView);
  const root = {
    ...styles.root,
    '&:hover': {
      bgColor: onClick ? '$background-secondary' : '',
    },
  };

  const onAction = async (action: Key) => {
    if (action === 'delete') {
      setIsConfirm(true);
    }
    if (action === 'duplicate') {
      createView && createView();
    }
    if (action === 'rename') {
      setIsRename(true);
    }
  };

  const onRename = () => {
    setIsRename(false);
    updateView && updateView(rename);
  };

  const label = (
    <>
      <AccentBox bgColor={accentColor} />
      <Flex orientation="vertical" justify="center" css={styles.option}>
        <Typography variant="subtitle" as="div" css={styles.name}>
          {name}
        </Typography>
        {(totalResults || totalResults === 0) && (
          <Typography variant="subtext" css={{ color: '$text-tertiary' }}>
            {formatCount(totalResults)}
          </Typography>
        )}
      </Flex>
    </>
  );

  useEffect(() => {
    if (!isEdit && isRename) {
      setIsRename(false);
    }
    if (!isRename && name !== rename) {
      setRename(name);
    }
  }, [isEdit, isRename, name, rename]);

  if (isRename) {
    return (
      <Box css={{ ...root, cursor: 'default' }}>
        <Flex align="center" gap="small">
          <AccentBox bgColor={accentColor} />
          <Flex orientation="vertical" justify="center" css={styles.option}>
            <TextField
              aria-label={t('core.renameView')}
              inputProps={{ style: { paddingRight: 44 } }}
              onChange={setRename}
              size="small"
              validationState={rename.length <= 40 ? 'valid' : 'invalid'}
              value={rename}
              endIcon={
                <Typography variant="subtext" css={{ fontSize: 12 }}>
                  {`${rename.length}/40`}
                </Typography>
              }
            />
          </Flex>
          <IconButton
            aria-label={t('core.cancelRenameView')}
            size="small"
            variant="secondary"
            onPress={() => setIsRename(false)}
          >
            <Icon icon="close" />
          </IconButton>
          <IconButton
            aria-label={t('core.confirmRenameView')}
            size="small"
            isDisabled={rename.length > 40}
            onPress={onRename}
          >
            <Icon icon="check" />
          </IconButton>
        </Flex>
      </Box>
    );
  }

  if (isEdit) {
    return (
      <Box data-testid="toggle-option" css={{ ...root, cursor: 'default' }}>
        <ConfirmModal
          title={t('core.deleteViewWarningTitle')}
          description={t('core.deleteViewBody', { viewName: name })}
          isOpen={isConfirm}
          onCancel={() => setIsConfirm(false)}
          customConfirmButton={
            <Button
              aria-label={t('core.deleteView')}
              variant="danger"
              onPress={deleteView}
            >
              {t('global.delete')}
            </Button>
          }
        />
        <Flex align="center" gap="small">
          {label}
          {isLocked && (
            <IconButton variant="tertiary" size="small" isDisabled>
              <Icon icon="lock" />
            </IconButton>
          )}
          {!isLocked && (
            <>
              {!disableDrag && (
                <Icon
                  aria-label={t('core.dragHandleLabel', { name })}
                  className="dragHandle"
                  icon="drag_indicator"
                  css={{
                    cursor: 'grab',
                    fontSize: '$large',
                    userSelect: 'auto',
                  }}
                />
              )}
              <Dropdown>
                <IconButton
                  aria-label={t('core.showActionsFor', { name })}
                  variant="secondary"
                  size="small"
                >
                  <Icon icon="more_vert" />
                </IconButton>
                <DropdownMenu onAction={onAction}>
                  <DropdownItem aria-label={t('core.renameView')} key="rename">
                    {t('global.rename')}
                  </DropdownItem>
                  <DropdownItem
                    aria-label={t('core.duplicateView')}
                    key="duplicate"
                  >
                    {t('global.duplicate')}
                  </DropdownItem>
                  <DropdownSection>
                    <DropdownItem
                      aria-label={t('core.deleteView')}
                      key="delete"
                      css={{ color: '$text-danger' }}
                    >
                      {t('global.delete')}
                    </DropdownItem>
                  </DropdownSection>
                </DropdownMenu>
              </Dropdown>
            </>
          )}
        </Flex>
      </Box>
    );
  }

  return (
    <Box data-testid="toggle-option" onClick={onClick} css={root}>
      <Flex align="center" gap="small">
        {label}
        {isSelected && <Icon icon="check" />}
      </Flex>
    </Box>
  );
}

export default ViewToggleOption;
